import React, { useState, useEffect } from 'react';
import { IoClose, IoPaperPlaneOutline, IoCloudUploadOutline, IoTrashOutline, IoWarningOutline } from 'react-icons/io5';

const DAYS_COUNT = 30;
const defaultDayEntry = { type: 'text', content: '', caption: '' };

// Базовые URL для запросов
const API_BASE_URL = 'https://ut-ka.ru/api';

const ScheduleEditor = () => {
  const [schedule, setSchedule] = useState({});
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('');
  const [sending, setSending] = useState(false);
  const [editDay, setEditDay] = useState(null);
  const [editData, setEditData] = useState({ ...defaultDayEntry });
  const [uploading, setUploading] = useState(false);
  const [showBulkSendModal, setShowBulkSendModal] = useState(false);
  const [showTestSendModal, setShowTestSendModal] = useState(false);
  const [showImmediateSendModal, setShowImmediateSendModal] = useState(false);
  const [sendDay, setSendDay] = useState(1);
  const [sendStatus, setSendStatus] = useState('');
  const [bulkSendProgress, setBulkSendProgress] = useState(0);
  const [bulkSendTotal, setBulkSendTotal] = useState(0);
  const [bulkSendCurrent, setBulkSendCurrent] = useState(0);
  
  // Для немедленной рассылки
  const [immediateMessage, setImmediateMessage] = useState({
    type: 'text',
    content: '',
    caption: ''
  });

  // === Загрузка расписания при монтировании компонента ===
  useEffect(() => {
    fetchOrCreateSchedule();
  }, []);

  const fetchOrCreateSchedule = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/schedule`);
      if (response.ok) {
        const data = await response.json();
        if (Object.keys(data).length === 0) {
          console.log('Пустые данные, создаём новое расписание');
          const emptySchedule = Array.from({ length: DAYS_COUNT }, (_, i) => i + 1).reduce(
            (acc, day) => ({ ...acc, [day]: { ...defaultDayEntry } }),
            {}
          );
          setSchedule(emptySchedule);
          await saveScheduleToServer(emptySchedule);
        } else {
          setSchedule(data);
        }
      } else if (response.status === 404) {
        console.log('Файл не найден, создаём пустое расписание');
        const emptySchedule = Array.from({ length: DAYS_COUNT }, (_, i) => i + 1).reduce(
          (acc, day) => ({ ...acc, [day]: { ...defaultDayEntry } }),
          {}
        );
        setSchedule(emptySchedule);
        await saveScheduleToServer(emptySchedule);
      } else {
        throw new Error(`Ошибка загрузки: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Ошибка при загрузке расписания:', err);
      alert('Не удалось загрузить расписание: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // === Сохранение расписания на сервер ===
  const saveScheduleToServer = async (scheduleToSave) => {
    try {
      const response = await fetch(`${API_BASE_URL}/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scheduleToSave),
      });

      if (!response.ok) {
        throw new Error(`Ошибка сохранения: ${response.statusText}`);
      }

      return true;
    } catch (err) {
      console.error('Ошибка при сохранении расписания:', err);
      alert('Ошибка сохранения: ' + err.message);
      return false;
    }
  };

  // === Удаление сообщения дня ===
  const handleDeleteDay = (day) => {
    if (window.confirm(`Вы уверены, что хотите удалить сообщение дня ${day}?`)) {
      const updatedSchedule = {
        ...schedule,
        [day]: { ...defaultDayEntry }
      };
      setSchedule(updatedSchedule);
      
      // Сохраняем на сервере
      saveScheduleToServer(updatedSchedule).then(success => {
        if (success) {
          setSaveStatus(`✅ Сообщение дня ${day} удалено`);
          setTimeout(() => setSaveStatus(''), 3000);
        }
      });
    }
  };

  // === Работа с модальным окном редактирования ===
  const openEditModal = (day) => {
    setEditDay(day);
    setEditData(schedule[day] || { ...defaultDayEntry });
  };

  const closeEditModal = () => {
    if (uploading) {
      alert("Подождите, идёт загрузка файла!");
      return;
    }
    setEditDay(null);
    setEditData({ ...defaultDayEntry });
  };

  const handleFieldChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  // === Загрузка файла (фото/видео) для редактирования дня ===
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isPhoto = editData.type === 'photo';
    const isVideo = editData.type === 'video';

    if (!isPhoto && !isVideo) {
      alert('Загрузка файлов возможна только для типа фото или видео');
      return;
    }

    if ((isPhoto && !file.type.startsWith('image/')) || (isVideo && !file.type.startsWith('video/'))) {
      alert(`Пожалуйста, выберите корректный ${isPhoto ? 'фото' : 'видео'} файл.`);
      return;
    }

    // Проверка размера файла (макс 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`Файл слишком большой. Максимальный размер: 10MB. Ваш файл: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.url) {
        setEditData((prev) => ({ ...prev, content: data.url }));
        alert('Файл успешно загружен!');
      } else {
        alert('Ошибка загрузки: ' + (data.error || 'Неизвестная ошибка'));
      }
    } catch (error) {
      console.error('Ошибка при загрузке файла:', error);
      alert('Произошла ошибка при загрузке файла');
    } finally {
      setUploading(false);
    }
  };

  // === Загрузка файла для немедленной рассылки ===
  const handleImmediateFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isPhoto = immediateMessage.type === 'photo';
    const isVideo = immediateMessage.type === 'video';

    if (!isPhoto && !isVideo) {
      alert('Загрузка файлов возможна только для типа фото или видео');
      return;
    }

    // Проверка типа файла
    if ((isPhoto && !file.type.startsWith('image/')) || 
        (isVideo && !file.type.startsWith('video/'))) {
      alert(`Пожалуйста, выберите корректный ${isPhoto ? 'фото' : 'видео'} файл.`);
      return;
    }

    // Проверка размера файла (макс 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`Файл слишком большой. Максимальный размер: 10MB. Ваш файл: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      return;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Ошибка сервера: ${response.status}`);
      }

      const data = await response.json();

      if (data.url) {
        setImmediateMessage(prev => ({ ...prev, content: data.url }));
        setSendStatus('✅ Файл успешно загружен!');
        setTimeout(() => setSendStatus(''), 3000);
      } else {
        throw new Error(data.error || 'Неизвестная ошибка загрузки');
      }
    } catch (error) {
      console.error('Ошибка при загрузке файла:', error);
      setSendStatus(`❌ Ошибка загрузки: ${error.message}`);
      setTimeout(() => setSendStatus(''), 5000);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // === Тестовая отправка сообщения ===
  const handleTestSend = async () => {
    if (!schedule[sendDay] || !schedule[sendDay].content.trim()) {
      setSendStatus('❌ Нет сообщения для этого дня');
      return;
    }

    setSending(true);
    setSendStatus('⏳ Отправка тестового сообщения...');

    try {
      const response = await fetch(`${API_BASE_URL}/send-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetUser: "YOUR_TEST_USER_ID", // Замените на ваш тестовый chat ID
          message: schedule[sendDay]
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSendStatus('✅ Тестовое сообщение отправлено!');
      } else {
        setSendStatus(`❌ Ошибка: ${data.error || 'Неизвестная ошибка'}`);
      }
    } catch (error) {
      console.error('Ошибка тестовой отправки:', error);
      setSendStatus(`❌ Ошибка: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  // === Немедленная массовая рассылка всем пользователям ===
  const handleImmediateSend = async () => {
    if (!immediateMessage.content.trim()) {
      setSendStatus('❌ Введите текст сообщения или загрузите файл');
      return;
    }

    setSending(true);
    setSendStatus('⏳ Начинаем массовую рассылку всем пользователям...');
    setBulkSendProgress(0);
    setBulkSendCurrent(0);

    try {
      // Получаем всех пользователей
      const response = await fetch(`${API_BASE_URL}/users-stats`);
      const stats = await response.json();
      const totalUsers = stats.total || 0;
      
      setBulkSendTotal(totalUsers);

      if (totalUsers === 0) {
        setSendStatus('❌ Нет пользователей для рассылки');
        setSending(false);
        return;
      }

      // Отправляем запрос на массовую рассылку
      const sendResponse = await fetch(`${API_BASE_URL}/send-immediate-broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: immediateMessage
        }),
      });

      const data = await sendResponse.json();

      if (sendResponse.ok && data.success) {
        setSendStatus(`✅ Массовая рассылка завершена! Отправлено: ${data.sent || 0} пользователям`);
        setBulkSendProgress(100);
        setBulkSendCurrent(totalUsers);
        
        // Очищаем форму
        setImmediateMessage({
          type: 'text',
          content: '',
          caption: ''
        });
        
        // Закрываем модалку через 3 секунды
        setTimeout(() => {
          setShowImmediateSendModal(false);
        }, 3000);
      } else {
        setSendStatus(`❌ Ошибка: ${data.error || 'Неизвестная ошибка'}`);
      }
    } catch (error) {
      console.error('Ошибка массовой рассылки:', error);
      setSendStatus(`❌ Ошибка: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  // === Рассылка расписания (по дням обучения) ===
  const handleScheduleSend = async () => {
    if (Object.values(schedule).filter(day => day.content.trim()).length === 0) {
      setSendStatus('❌ Нет сообщений для рассылки');
      return;
    }

    setSending(true);
    setSendStatus('⏳ Начинаем рассылку расписания...');
    setBulkSendProgress(0);
    setBulkSendCurrent(0);

    try {
      // Получаем количество пользователей
      const statsResponse = await fetch(`${API_BASE_URL}/users-stats`);
      const stats = await statsResponse.json();
      const totalUsers = stats.total || 0;
      
      setBulkSendTotal(totalUsers);

      // Отправляем запрос на рассылку расписания
      const response = await fetch(`${API_BASE_URL}/send-bulk-messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: schedule,
          type: 'full'
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSendStatus(`✅ Рассылка расписания завершена! Отправлено: ${data.sent || 0} сообщений`);
        setBulkSendProgress(100);
        setBulkSendCurrent(totalUsers);
        
        // Закрываем модалку через 3 секунды
        setTimeout(() => {
          setShowBulkSendModal(false);
        }, 3000);
      } else {
        setSendStatus(`❌ Ошибка: ${data.error || 'Неизвестная ошибка'}`);
      }
    } catch (error) {
      console.error('Ошибка рассылки расписания:', error);
      setSendStatus(`❌ Ошибка: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  // === Рассылка конкретного дня ===
  const handleDaySend = async (day) => {
    if (!schedule[day] || !schedule[day].content.trim()) {
      setSendStatus(`❌ Нет сообщения для дня ${day}`);
      return;
    }

    setSending(true);
    setSendStatus(`⏳ Рассылка дня ${day} всем пользователям...`);

    try {
      const response = await fetch(`${API_BASE_URL}/send-bulk-messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: { [day]: schedule[day] },
          type: 'day',
          day: day
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSendStatus(`✅ Рассылка дня ${day} завершена! Отправлено: ${data.sent || 0} пользователям`);
      } else {
        setSendStatus(`❌ Ошибка: ${data.error || 'Неизвестная ошибка'}`);
      }
    } catch (error) {
      console.error('Ошибка рассылки дня:', error);
      setSendStatus(`❌ Ошибка: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  // === Сохранение изменений дня ===
  const saveDay = async () => {
    const updatedSchedule = {
      ...schedule,
      [editDay]: { ...editData },
    };
    setSchedule(updatedSchedule);
    
    setSaveStatus('Сохраняем...');
    const success = await saveScheduleToServer(updatedSchedule);
    if (success) {
      setSaveStatus('Расписание успешно сохранено');
      setTimeout(() => setSaveStatus(''), 3000);
    } else {
      setSaveStatus('Ошибка при сохранении');
      setTimeout(() => setSaveStatus(''), 3000);
    }
    
    closeEditModal();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

return (
  <div className="h-screen overflow-y-auto overscroll-contain">
    <div className="p-4 max-w-6xl mx-auto pb-24">

      {/* Заголовок и кнопки управления */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Редактор расписания рассылки</h2>
          <p className="text-gray-400 mt-1">30 дней обучения</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowImmediateSendModal(true)}
            disabled={sending}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
          >
            <IoPaperPlaneOutline /> Немедленная рассылка
          </button>
          
          <button
            onClick={() => setShowTestSendModal(true)}
            disabled={sending}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
          >
            <IoPaperPlaneOutline /> Тестовая отправка
          </button>
          
          <button
            onClick={() => setShowBulkSendModal(true)}
            disabled={sending}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
          >
            <IoPaperPlaneOutline /> Рассылка расписания
          </button>
        </div>
      </div>

      {/* Статус сообщения */}
      {(saveStatus || sendStatus) && (
        <div className={`mb-4 p-3 rounded-lg text-white ${
          saveStatus?.includes('успешно') || sendStatus?.includes('✅') ? 'bg-green-900/30 border border-green-700' :
          saveStatus?.includes('Ошибка') || sendStatus?.includes('❌') ? 'bg-red-900/30 border border-red-700' :
          'bg-blue-900/30 border border-blue-700'
        }`}>
          {saveStatus || sendStatus}
        </div>
      )}

      {/* Прогресс массовой рассылки */}
      {bulkSendProgress > 0 && (
        <div className="mb-4 bg-gray-800 rounded-lg p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-white font-medium">Прогресс рассылки</span>
            <span className="text-gray-300">{bulkSendCurrent}/{bulkSendTotal}</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2.5">
            <div 
              className="bg-green-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${bulkSendProgress}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Грид дней */}
      <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 mb-6">

        {[...Array(DAYS_COUNT)].map((_, i) => {
          const day = i + 1;
          const dayData = schedule[day] || { ...defaultDayEntry };
          const hasContent = dayData.content?.trim();
          const typeIcon = dayData.type === 'text' ? '📝' : dayData.type === 'photo' ? '🖼' : '🎬';
          
          return (
            <div key={day} className="relative group">
<button
  onClick={() => openEditModal(day)}
  className={`w-full aspect-square rounded-md flex flex-col items-center justify-center transition-all text-sm ${
    hasContent 
      ? 'bg-gradient-to-br from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800' 
      : 'bg-gray-800 hover:bg-gray-700'
  } border ${editDay === day ? 'border-blue-500' : 'border-gray-700'}`}
>

                <span className="text-sm font-semibold text-white leading-none">
  {day}
</span>

                {hasContent && (
                  <span className="text-sm mt-1">{typeIcon}</span>
                )}
              </button>
              
              {/* Кнопка удаления дня */}
              {hasContent && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteDay(day);
                  }}
                  className="absolute -top-1 -left-1 w-6 h-6 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Удалить сообщение"
                >
                  <IoTrashOutline size={10} />
                </button>
              )}
              
              {/* Кнопка отправки дня */}
              {hasContent && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDaySend(day);
                  }}
                  disabled={sending}
                  className="absolute -top-1 -right-1 w-6 h-6 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                  title="Отправить этот день всем"
                >
                  <IoPaperPlaneOutline size={10} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Статистика */}
      <div className="bg-gray-800 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold text-white mb-3">Статистика расписания</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-900 p-3 rounded">
            <div className="text-gray-400 text-sm">Всего дней</div>
            <div className="text-2xl font-bold text-white">{DAYS_COUNT}</div>
          </div>
          <div className="bg-gray-900 p-3 rounded">
            <div className="text-gray-400 text-sm">Заполнено</div>
            <div className="text-2xl font-bold text-green-500">
              {Object.values(schedule).filter(day => day.content?.trim()).length}
            </div>
          </div>
          <div className="bg-gray-900 p-3 rounded">
            <div className="text-gray-400 text-sm">Текстовых</div>
            <div className="text-2xl font-bold text-blue-500">
              {Object.values(schedule).filter(day => day.type === 'text' && day.content?.trim()).length}
            </div>
          </div>
          <div className="bg-gray-900 p-3 rounded">
            <div className="text-gray-400 text-sm">Медиа</div>
            <div className="text-2xl font-bold text-purple-500">
              {Object.values(schedule).filter(day => (day.type === 'photo' || day.type === 'video') && day.content?.trim()).length}
            </div>
          </div>
        </div>
      </div>

      {/* Инструкция - улучшенная видимость */}
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 mb-6 shadow-lg">
        <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <IoWarningOutline className="text-yellow-500" />
          Как использовать:
        </h3>
        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 mt-2 rounded-full bg-blue-500 flex-shrink-0"></div>
            <p className="text-gray-200">Нажмите на день, чтобы отредактировать сообщение</p>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 mt-2 rounded-full bg-blue-500 flex-shrink-0"></div>
            <p className="text-gray-200">Для фото и видео можно загрузить файл или указать URL</p>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 mt-2 rounded-full bg-blue-500 flex-shrink-0"></div>
            <p className="text-gray-200">Красная кнопка <IoTrashOutline className="inline text-red-500" /> удаляет сообщение дня</p>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 mt-2 rounded-full bg-blue-500 flex-shrink-0"></div>
            <p className="text-gray-200">Синяя кнопка <IoPaperPlaneOutline className="inline text-blue-500" /> отправляет день всем пользователям</p>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 mt-2 rounded-full bg-blue-500 flex-shrink-0"></div>
            <p className="text-gray-200">
              <span className="font-semibold text-purple-400">Немедленная рассылка</span> - отправляет произвольное сообщение (текст/фото/видео) всем пользователям сейчас
            </p>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 mt-2 rounded-full bg-blue-500 flex-shrink-0"></div>
            <p className="text-gray-200">
              <span className="font-semibold text-green-400">Рассылка расписания</span> - отправляет сообщения согласно дню обучения пользователя
            </p>
          </div>
        </div>
      </div>

      {/* Модальное окно редактирования */}
      {editDay !== null && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-900 border-b border-gray-700 p-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">Редактирование дня {editDay}</h3>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-white"
                disabled={uploading}
              >
                <IoClose size={24} />
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Тип сообщения
                  </label>
                  <select
                    value={editData.type}
                    onChange={(e) => handleFieldChange('type', e.target.value)}
                    className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="text">📝 Текст</option>
                    <option value="photo">🖼 Фото</option>
                    <option value="video">🎬 Видео</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    {editData.type === 'text' ? 'Текст сообщения' : 'Содержимое'}
                  </label>
                  
                  {editData.type === 'text' ? (
                    <textarea
                      rows={6}
                      value={editData.content}
                      onChange={(e) => handleFieldChange('content', e.target.value)}
                      className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white resize-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                      placeholder="Введите текст сообщения..."
                    />
                  ) : (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editData.content}
                        onChange={(e) => handleFieldChange('content', e.target.value)}
                        placeholder="Введите URL или загрузите файл"
                        className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                      />
                      
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">
                          Загрузить файл
                        </label>
                        <div className="flex items-center gap-2">
                          <label className="flex-1 cursor-pointer">
                            <input
                              type="file"
                              accept={editData.type === 'photo' ? 'image/*' : 'video/*'}
                              onChange={handleFileChange}
                              className="hidden"
                              disabled={uploading}
                            />
                            <div className={`p-3 rounded-lg border text-center transition-colors ${
                              uploading 
                                ? 'bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed' 
                                : 'bg-gray-800 border-gray-700 hover:border-blue-500 text-white cursor-pointer'
                            }`}>
                              {uploading ? (
                                <span className="text-yellow-400 flex items-center justify-center gap-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-400"></div>
                                  Загрузка...
                                </span>
                              ) : (
                                <span className="flex items-center justify-center gap-2">
                                  <IoCloudUploadOutline size={18} /> Выбрать файл
                                </span>
                              )}
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {(editData.type === 'photo' || editData.type === 'video') && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-white mb-2">
                    Подпись (caption)
                  </label>
                  <textarea
                    rows={3}
                    value={editData.caption}
                    onChange={(e) => handleFieldChange('caption', e.target.value)}
                    className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white resize-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    placeholder="Введите подпись к медиафайлу..."
                  />
                </div>
              )}
              
              {/* Предпросмотр */}
              {editData.content && (
                <div className="mt-6 p-4 bg-gray-800 rounded-lg">
                  <h4 className="text-sm font-medium text-white mb-2">Предпросмотр:</h4>
                  {editData.type === 'text' ? (
                    <div className="p-3 bg-gray-900 rounded text-white whitespace-pre-wrap">
                      {editData.content}
                    </div>
                  ) : editData.type === 'photo' ? (
                    <div className="space-y-2">
                      <img 
                        src={editData.content} 
                        alt="Preview" 
                        className="max-w-full max-h-64 rounded-lg mx-auto"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                      {editData.caption && (
                        <div className="p-3 bg-gray-900 rounded text-white">
                          {editData.caption}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="p-4 bg-gray-900 rounded text-center">
                        <span className="text-white">🎬 Видео: {editData.content}</span>
                      </div>
                      {editData.caption && (
                        <div className="p-3 bg-gray-900 rounded text-white">
                          {editData.caption}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => handleDeleteDay(editDay)}
                  className="px-6 py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium"
                >
                  <IoTrashOutline className="inline mr-2" />
                  Удалить
                </button>
                
                <button
                  onClick={closeEditModal}
                  disabled={uploading}
                  className="px-6 py-3 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-medium disabled:opacity-50"
                >
                  Отмена
                </button>
                
                <button
                  onClick={saveDay}
                  disabled={uploading || !editData.content.trim()}
                  className="px-6 py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Сохранить
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно немедленной рассылки */}
      {showImmediateSendModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">Немедленная массовая рассылка</h3>
              <button
                onClick={() => setShowImmediateSendModal(false)}
                className="text-gray-400 hover:text-white"
                disabled={sending || uploading}
              >
                <IoClose size={24} />
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-6">
                <div className="p-4 bg-yellow-900/20 border border-yellow-700 rounded-lg">
                  <h4 className="text-lg font-semibold text-yellow-400 mb-2 flex items-center gap-2">
                    <IoWarningOutline /> Внимание!
                  </h4>
                  <p className="text-gray-300">
                    Это сообщение будет отправлено ВСЕМ пользователям сразу, независимо от их дня обучения.
                    Используйте для срочных уведомлений, новостей или общих сообщений.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Тип сообщения
                    </label>
                    <select
                      value={immediateMessage.type}
                      onChange={(e) => setImmediateMessage(prev => ({...prev, type: e.target.value, content: '', caption: ''}))}
                      className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white"
                      disabled={uploading}
                    >
                      <option value="text">📝 Текст</option>
                      <option value="photo">🖼 Фото</option>
                      <option value="video">🎬 Видео</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      {immediateMessage.type === 'text' ? 'Текст сообщения' : 'Содержимое'}
                    </label>
                    
                    {immediateMessage.type === 'text' ? (
                      <textarea
                        rows={6}
                        value={immediateMessage.content}
                        onChange={(e) => setImmediateMessage(prev => ({...prev, content: e.target.value}))}
                        className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white resize-none"
                        placeholder="Введите текст для рассылки..."
                        disabled={uploading}
                      />
                    ) : (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={immediateMessage.content}
                          onChange={(e) => setImmediateMessage(prev => ({...prev, content: e.target.value}))}
                          placeholder="Введите URL файла или загрузите файл"
                          className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white"
                          disabled={uploading}
                        />
                        
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            Загрузить файл
                          </label>
                          <div className="flex items-center gap-2">
                            <label className="flex-1 cursor-pointer">
                              <input
                                type="file"
                                accept={immediateMessage.type === 'photo' ? 'image/*' : 'video/*'}
                                onChange={handleImmediateFileUpload}
                                className="hidden"
                                disabled={uploading}
                              />
                              <div className={`p-3 rounded-lg border text-center transition-colors ${
                                uploading 
                                  ? 'bg-gray-700 border-gray-600 text-gray-400 cursor-not-allowed' 
                                  : 'bg-gray-800 border-gray-700 hover:border-blue-500 text-white cursor-pointer'
                              }`}>
                                {uploading ? (
                                  <span className="text-yellow-400 flex items-center justify-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-400"></div>
                                    Загрузка...
                                  </span>
                                ) : (
                                  <span className="flex items-center justify-center gap-2">
                                    <IoCloudUploadOutline size={18} /> Выбрать файл
                                  </span>
                                )}
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {(immediateMessage.type === 'photo' || immediateMessage.type === 'video') && (
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Подпись (caption)
                    </label>
                    <textarea
                      rows={3}
                      value={immediateMessage.caption}
                      onChange={(e) => setImmediateMessage(prev => ({...prev, caption: e.target.value}))}
                      className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white resize-none"
                      placeholder="Введите подпись..."
                      disabled={uploading}
                    />
                  </div>
                )}
                
                {/* Предпросмотр */}
                {immediateMessage.content && (
                  <div className="p-4 bg-gray-800 rounded-lg">
                    <h4 className="text-sm font-medium text-white mb-2">Предпросмотр сообщения:</h4>
                    {immediateMessage.type === 'text' ? (
                      <div className="p-3 bg-gray-900 rounded text-white whitespace-pre-wrap">
                        {immediateMessage.content}
                      </div>
                    ) : immediateMessage.type === 'photo' ? (
                      <div className="space-y-2">
                        <div className="flex flex-col items-center">
                          <img 
                            src={immediateMessage.content} 
                            alt="Preview" 
                            className="max-w-full max-h-64 rounded-lg"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = 
                                `<div class="p-4 bg-gray-900 rounded text-center text-gray-400">
                                  🖼 Фото (URL: ${immediateMessage.content})
                                </div>`;
                            }}
                          />
                        </div>
                        {immediateMessage.caption && (
                          <div className="p-3 bg-gray-900 rounded text-white">
                            <strong>Подпись:</strong> {immediateMessage.caption}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="p-4 bg-gray-900 rounded text-center">
                          <span className="text-white">🎬 Видео: {immediateMessage.content}</span>
                        </div>
                        {immediateMessage.caption && (
                          <div className="p-3 bg-gray-900 rounded text-white">
                            <strong>Подпись:</strong> {immediateMessage.caption}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {sendStatus && (
                  <div className={`p-4 rounded-lg ${
                    sendStatus.includes('✅') ? 'bg-green-900/30 text-green-300' :
                    sendStatus.includes('❌') ? 'bg-red-900/30 text-red-300' :
                    'bg-blue-900/30 text-blue-300'
                  }`}>
                    {sendStatus}
                  </div>
                )}
                
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => setShowImmediateSendModal(false)}
                    disabled={sending || uploading}
                    className="px-6 py-3 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-medium disabled:opacity-50"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={handleImmediateSend}
                    disabled={sending || uploading || !immediateMessage.content.trim()}
                    className="px-6 py-3 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sending ? 'Отправка...' : 'Отправить всем'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно тестовой отправки */}
      {showTestSendModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl max-w-md w-full">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">Тестовая отправка</h3>
              <button
                onClick={() => setShowTestSendModal(false)}
                className="text-gray-400 hover:text-white"
                disabled={sending}
              >
                <IoClose size={24} />
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Выберите день для отправки
                  </label>
                  <select
                    value={sendDay}
                    onChange={(e) => setSendDay(parseInt(e.target.value))}
                    className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white"
                  >
                    {[...Array(DAYS_COUNT)].map((_, i) => {
                      const day = i + 1;
                      const hasContent = schedule[day]?.content?.trim();
                      return (
                        <option key={day} value={day} disabled={!hasContent}>
                          День {day} {hasContent ? '' : '(пусто)'}
                        </option>
                      );
                    })}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Chat ID получателя
                  </label>
                  <input
                    type="text"
                    placeholder="Введите Telegram Chat ID"
                    className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white"
                    defaultValue="YOUR_TEST_USER_ID"
                  />
                  <p className="text-sm text-gray-400 mt-1">
                    Укажите свой Telegram Chat ID для теста
                  </p>
                </div>
                
                {sendStatus && (
                  <div className={`p-3 rounded-lg ${
                    sendStatus.includes('✅') ? 'bg-green-900/30 text-green-300' :
                    sendStatus.includes('❌') ? 'bg-red-900/30 text-red-300' :
                    'bg-blue-900/30 text-blue-300'
                  }`}>
                    {sendStatus}
                  </div>
                )}
                
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => setShowTestSendModal(false)}
                    disabled={sending}
                    className="px-6 py-3 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-medium"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={handleTestSend}
                    disabled={sending || !schedule[sendDay]?.content.trim()}
                    className="px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50"
                  >
                    {sending ? 'Отправка...' : 'Отправить тест'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно рассылки расписания */}
      {showBulkSendModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl max-w-md w-full">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">Рассылка расписания</h3>
              <button
                onClick={() => setShowBulkSendModal(false)}
                className="text-gray-400 hover:text-white"
                disabled={sending}
              >
                <IoClose size={24} />
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div className="p-4 bg-green-900/20 border border-green-700 rounded-lg">
                  <h4 className="text-lg font-semibold text-green-400 mb-2">Рассылка по дням обучения</h4>
                  <p className="text-gray-300 text-sm">
                    Эта операция отправит сообщения пользователям согласно их дню обучения.
                    Каждый пользователь получит сообщение дня, соответствующего его дате регистрации.
                  </p>
                </div>
                
                <div className="bg-gray-800 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-2">Статистика рассылки:</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-gray-400 text-sm">Всего дней</div>
                      <div className="text-xl font-bold text-white">{Object.values(schedule).filter(day => day.content?.trim()).length}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-sm">Готово к отправке</div>
                      <div className="text-xl font-bold text-green-500">
                        {Object.values(schedule).filter(day => day.content?.trim()).length}
                      </div>
                    </div>
                  </div>
                </div>
                
                {sendStatus && (
                  <div className={`p-3 rounded-lg ${
                    sendStatus.includes('✅') ? 'bg-green-900/30 text-green-300' :
                    sendStatus.includes('❌') ? 'bg-red-900/30 text-red-300' :
                    'bg-blue-900/30 text-blue-300'
                  }`}>
                    {sendStatus}
                  </div>
                )}
                
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => setShowBulkSendModal(false)}
                    disabled={sending}
                    className="px-6 py-3 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-medium"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={handleScheduleSend}
                    disabled={sending || Object.values(schedule).filter(day => day.content?.trim()).length === 0}
                    className="px-6 py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium disabled:opacity-50"
                  >
                    {sending ? 'Рассылка...' : 'Начать рассылку'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>    </div>
  );
};

export default ScheduleEditor;